<template>
    <main class="p-2 md:p-5 w-full h-full max-h-main flex flex-col gap-2 items-start min-h-main">
        <div class="flex justify-end w-full">
            <app-button
                size="mini"
                @click="removeSubscription()"
                variant="danger"
                aria-label="remove subscription"
                icon="icon-trash-can-outline"
                :label="$t('commons.removeShort')"
            />
        </div>
        <div class="flex w-full flex-grow flex-col relative items-center">
            <div v-if="loading" class="flex justify-center">
                <icon-rotate-right class="animate animate-spin"></icon-rotate-right>
            </div>
            <div v-else class="flex flex-col gap-2 w-1/2">
                <app-separator>{{ $t('subscriptions.title') }}</app-separator>
                <table>
                    <tr>
                        <th class="p-2 text-right">
                            <app-label :label="$t('subscriptions.code') + ' : '" for="code"></app-label>
                        </th>
                        <td class="p-2">
                            <input id="for" :value="subscription.code" type="text" size="26" readonly="readonly" />
                        </td>
                    </tr>
                    <tr>
                        <th class="p-2 text-right align-top">
                            <app-label :label="$t('subscriptions.owner') + ' : '"></app-label>
                        </th>
                        <td class="p-2">
                            <div class="flex flex-col">
                                {{ subscription.owner.firstName }} {{ subscription.owner.lastName }}
                                <a :href="'mailto:' + subscription.owner.email">
                                    {{ subscription.owner.email }}
                                </a>
                                <app-phone :number="subscription.owner.phone" />
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <th class="p-2 text-right align-top">
                            <app-label :label="$t('subscriptions.organization') + ' : '"></app-label>
                        </th>
                        <td class="p-2">
                            <div class="flex flex-col">
                                <span class="text-xs">{{ subscription.organization.siret }}</span>
                                {{ subscription.organization.name }} ({{ subscription.organization.city }})
                                <a :href="'mailto:' + subscription.organization.email">
                                    {{ subscription.organization.email }}
                                </a>
                                <app-phone :number="subscription.organization.phone" />
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <th class="p-2 text-right">
                            <app-label :label="$t('subscriptions.createdAt') + ' : '"></app-label>
                        </th>
                        <td class="p-2">
                            {{ subscription.createdAt | humanizeDate }}
                        </td>
                    </tr>
                </table>

                <app-separator :label="$t('subscriptions.parameters')"></app-separator>

                <div class="flex gap-2 w-full">
                    <app-number-input
                        :label="$t('subscriptions.creditAmount') + ' : '"
                        v-model="subscription.creditAmount"
                        @blur="save"
                    ></app-number-input>
                </div>
                <div class="flex gap-2 w-full">
                    <app-select
                        :label="$t('organizations.paymentModality') + ' : '"
                        v-model="subscription.billingFrequency"
                        @input="save()"
                    >
                        <option value="monthly">{{ $t('organizations.frequencies.monthly') }}</option>
                        <option value="biannual">{{ $t('organizations.frequencies.biannual') }}</option>
                        <option value="annual">{{ $t('organizations.frequencies.annual') }}</option>
                    </app-select>
                </div>
                <div class="flex gap-2 w-full">
                    <app-date-input
                        :label="$t('subscriptions.firstPaymentAt') + ' : '"
                        v-model="subscription.firstPaymentAt"
                        @blur="save"
                    ></app-date-input>
                </div>
                <div class="flex gap-2 w-full">
                    <app-date-input
                        :label="$t('subscriptions.startDate') + ' : '"
                        v-model="subscription.startDate"
                        @blur="save"
                    ></app-date-input>
                </div>
                <div class="flex gap-2 w-full">
                    <app-date-input
                        :label="$t('subscriptions.endDate') + ' : '"
                        v-model="subscription.endDate"
                        @blur="save"
                    ></app-date-input>
                </div>

                <app-separator :label="$t('subscriptions.documents')"></app-separator>
                <div class="flex flex-col w-full gap-2 p-2">
                    <div class="flex gap-2 w-full">
                        <app-label :label="$t('subscriptions.emptyPDFSubscription') + ' : '"></app-label>
                        <icon-rotate-right
                            class="animate animate-spin"
                            v-if="subscriptionTemplateLoading"
                        ></icon-rotate-right>
                        <template v-else>
                            <div v-if="subscription.templateUrl" class="flex justify-between w-full">
                                <app-file-link
                                    icon="icon-file-pdf-outline"
                                    :url="subscription.templateUrl"
                                    file-name="Contrat.pdf"
                                    class="text-sm underline"
                                />
                                <app-button
                                    size="mini"
                                    :label="$t('subscriptions.regeneratePdf')"
                                    @click="generateSubscription"
                                ></app-button>
                            </div>
                            <div v-else class="flex">
                                <app-button
                                    size="mini"
                                    icon="icon-file-pdf-outline"
                                    :label="$t('subscriptions.generatePdf')"
                                    @click="generateSubscription"
                                ></app-button>
                            </div>
                        </template>
                    </div>
                    <div class="flex gap-2 flex-col" v-if="subscription.templateUrl">
                        <div class="flex gap-2">
                            <app-label :label="$t('subscriptions.signedPDFSubscription') + ' : '"></app-label>
                            <div class="flex justify-between w-full">
                                <app-file-link
                                    icon="icon-file-pdf-outline"
                                    :url="subscription.signedUrl"
                                    file-name="Contrat_signe.pdf"
                                    class="text-sm underline"
                                    v-if="subscription.signedUrl"
                                />
                                <app-upload-button
                                    class="text-sm"
                                    size="mini"
                                    icon="icon-paperclip"
                                    method="PUT"
                                    :label="
                                        subscription.signedUrl
                                            ? $t('subscriptions.uploadPDF')
                                            : $t('subscriptions.replacePDF')
                                    "
                                    @select="onSignedSubscriptionSelected"
                                    scope="subscriptions"
                                    :end-point="`/api/subscriptions/${$route.params.subscriptionId}/signedPdf`"
                                />
                            </div>
                        </div>
                        <app-date-link
                            v-model="subscription.signedAt"
                            :label="$t('subscriptions.signedAt')"
                            @input="save"
                        ></app-date-link>
                        <app-date-link
                            v-model="subscription.validatedAt"
                            :label="$t('subscriptions.validatedAt')"
                            @input="save"
                        ></app-date-link>
                    </div>
                </div>
            </div>
        </div>

        <app-popup ref="calculator" :showHeader="true" :title="$t('subscriptions.calculator')">
            <div class="p-2 flex flex-col"></div>
        </app-popup>
    </main>
</template>

<script>
import AppSelect from '@/components/appSelect/AppSelect';
import AppPopup from '@/components/app-popup/AppPopup';
import AppFooter from '@/components/appFooter/AppFooter';
import AppButton from '@/components/appButton/AppButton';
import AppInputText from '@/components/appInputText/AppInputText.vue';
import AppFilter from '@/components/appFilter/AppFilter.vue';
import AppCheckbox from '@/components/app-checkbox/AppCheckbox.vue';
import IconChevronLeft from '@/icons/IconChevronLeft.vue';
import AppSeparator from '@/components/appSeparator/AppSeparator.vue';
import AppDateLink from '@/components/appDateLink/AppDateLink.vue';
import {
    generateSubscriptionTemplate,
    getSubscription,
    removeSubscription,
    updateSubscription,
} from '@/features/subscriptions/subscriptions.service';
import AppLabel from '@/components/appLabel/AppLabel.vue';
import AppTextarea from '@/components/app-textarea/AppTextarea.vue';
import AppFileLink from '@/components/appFileLink/AppFileLink.vue';
import AppUploadButton from '@/components/app-uploadButton/AppUploadButton.vue';
import IconFilePdfOutline from '@/icons/IconFilePdfOutline.vue';
import AppPhone from '@/components/appPhone/AppPhone.vue';
import AppTipsIcon from '@/components/appTipsIcon/AppTipsIcon.vue';
import { confirm } from '@/features/dialogs/dialogs.service';
import AppMultipleUploadButton from '@/components/app-multipleUploadButton/AppMultipleUploadButton.vue';
import AppDropDownButton from '@/components/appDropDownButton/AppDropDownButton.vue';
import AppNumberLink from '@/components/appNumberLink/AppNumberLink.vue';
import { updateBreadCrumbs } from '@/state/state';
import AppNumberInput from '@/components/appNumberInput/AppNumberInput.vue';
import AppDateInput from '@/components/appDateInput/AppDateInput.vue';

export default {
    components: {
        AppDateInput,
        AppNumberInput,
        AppNumberLink,
        AppDropDownButton,
        AppMultipleUploadButton,
        AppTipsIcon,
        AppPhone,
        IconFilePdfOutline,
        AppUploadButton,
        AppFileLink,
        AppTextarea,
        AppLabel,
        AppDateLink,
        AppSeparator,
        IconChevronLeft,
        AppCheckbox,
        AppFilter,
        AppInputText,
        AppSelect,
        AppFooter,
        AppPopup,
        AppButton,
    },
    async created() {
        this.subscription = await getSubscription(this.$route.params.subscriptionId);
        updateBreadCrumbs({ subscriptionCode: this.subscription.code });
        this.loading = false;
    },
    methods: {
        async removeSubscription() {
            if (await confirm(this.$t('commons.confirmMessage'))) {
                await removeSubscription(this.subscription);
                this.$router.push({
                    name: this.$route.meta.parentName,
                    params: {
                        projectId: this.$route.params.projectId,
                    },
                });
            }
        },
        async generateSubscription() {
            if (!this.subscription.templateUrl || (await confirm(this.$t('subscriptions.replaceTemplate')))) {
                this.subscriptionTemplateLoading = true;
                const result = await generateSubscriptionTemplate(this.subscription);
                this.subscription.templateUrl = result.templateUrl;
                this.subscriptionTemplateLoading = false;
            }
        },
        async save() {
            const owner = this.subscription.owner;
            const organization = this.subscription.organization;
            this.subscription = await updateSubscription({
                id: this.subscription.id,
                creditAmount: this.subscription.creditAmount,
                amountHT: this.subscription.amountHT,
                billingFrequency: this.subscription.billingFrequency,
                type: this.subscription.type,

                proposedAt: this.subscription.proposedAt,
                signedAt: this.subscription.signedAt,

                validatedAt: this.subscription.validatedAt,
                startDate: this.subscription.startDate,
                endDate: this.subscription.endDate,
            });
            this.subscription.owner = owner;
            this.subscription.organization = organization;
        },
        async onSignedSubscriptionSelected(subscription) {
            this.subscription.signedUrl = subscription.signedUrl;
        },
    },
    data() {
        return {
            loading: true,
            subscriptionTemplateLoading: false,
            subscription: {},
            currencyFormat: {
                currency: 'EUR',
                currencyDisplay: 'symbol',
                style: 'currency',
                minimumFractionDigits: 2,
            },
            decimalFormat: {
                style: 'decimal',
                minimumFractionDigits: 2,
            },
        };
    },
};
</script>
